import { useEffect, useMemo, useState } from "react";
import { api, imgUrl, IMAGES } from "../lib/api";
import { Twitch, Youtube, Gamepad2, Users, MessageCircle, ExternalLink, Trophy, Play, Compass, Puzzle } from "lucide-react";
import Seo from "../components/Seo";

const GAME_META = {
  "synda-compass": {
    Icon: Compass,
    accent: "#FCA311",
    tagline: "Astronomical compass. Follow the stars. Find the fam.",
  },
  "luzz-puzz-mountain": {
    Icon: Puzzle,
    accent: "#E63946",
    tagline: "A landscape word puzzle. Climb the mountain word by word.",
  },
};

const PLATFORM_META = {
  pc: { label: "PC", color: "#FCA311" },
  xbox: { label: "XBOX", color: "#107C10" },
  ps: { label: "PS", color: "#006FCD" },
  switch: { label: "SWITCH", color: "#E60012" },
  mobile: { label: "MOBILE", color: "#E63946" },
};

export default function Gaming() {
  const [streamers, setStreamers] = useState([]);
  const [clips, setClips] = useState([]);
  const [squad, setSquad] = useState([]);
  const [vods, setVods] = useState([]);
  const [games, setGames] = useState([]);
  const [config, setConfig] = useState({ discord_invite_url: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/gaming/streamers").then((r) => r.data || []),
      api.get("/gaming/clips").then((r) => r.data || []),
      api.get("/gaming/squad").then((r) => r.data || []),
      api.get("/gaming/twitch-vods").then((r) => r.data || []).catch(() => []),
      api.get("/gaming/config").then((r) => r.data || {}),
      api.get("/games").then((r) => r.data || []).catch(() => []),
    ])
      .then(([st, cl, sq, vd, cf, gm]) => {
        setStreamers(st);
        setClips(cl);
        setSquad(sq);
        setVods(vd);
        setConfig(cf || {});
        setGames(gm);
      })
      .finally(() => setLoading(false));
  }, []);

  // Featured Twitch channel = first active twitch streamer
  const parentHost = typeof window !== "undefined" ? window.location.hostname : "";

  // Group clips by game
  const clipsByGame = useMemo(() => {
    const out = {};
    for (const c of clips) {
      const key = (c.game || "OTHER").trim().toUpperCase();
      if (!out[key]) out[key] = [];
      out[key].push(c);
    }
    return out;
  }, [clips]);

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="gaming-page">
      <Seo
        title="Gaming | SyndaFam Streams, Content Creators & Playable Games"
        description="SyndaFam gaming hub — Toronto hip-hop collective livestreams, Canadian creators, browser games and the crew grinding pixels between the music and video drops."
        path="/gaming"
      />
      {/* Living aurora backdrop + drifting orbs */}
      <div className="fixed inset-0 -z-10 bg-aurora pointer-events-none opacity-70" aria-hidden="true" />
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="orb orb-red orb-live"   style={{ width: 620, height: 620, top: "-160px", left: "-180px" }} />
        <div className="orb orb-amber orb-live" style={{ width: 520, height: 520, bottom: "-160px", right: "-140px", animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live"  style={{ width: 360, height: 360, top: "40%", left: "40%", opacity: 0.18, animationDelay: "-3s" }} />
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${IMAGES.texture})`, backgroundSize: "cover" }} />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-2">
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-4 text-glow-red reveal reveal-1">// THE ARCADE</div>
              <h1 className="font-display leading-[0.85] tracking-tighter flex items-center gap-4 flex-wrap reveal reveal-2" style={{ fontSize: "clamp(3rem, 9vw, 8rem)" }}>
                <Gamepad2 className="w-14 h-14 sm:w-20 sm:h-20 text-[#FCA311]" style={{ filter: "drop-shadow(0 0 24px rgba(252,163,17,0.55))" }} />
                ALL <span className="text-[#FCA311] text-glow-amber">GAS.</span>
              </h1>
              <div className="mt-8 max-w-xl glass-card-strong rounded-3xl p-6 reveal reveal-3">
                <p className="text-white/85 text-base sm:text-lg leading-relaxed">
                  Streams. Clips. Tournaments. The Fam squads up every night. Pull up.
                </p>
              </div>
            </div>
            {config.hero_youtube_id && (
              <div className="lg:col-span-3 rounded-3xl glass-card-strong ai-border overflow-hidden aspect-video float-card" data-testid="gaming-hero-video">
                <iframe
                  title="Gaming hero video"
                  src={`https://www.youtube.com/embed/${config.hero_youtube_id}?autoplay=1&mute=1&loop=1&playlist=${config.hero_youtube_id}&controls=1&rel=0&modestbranding=1&playsinline=1&cc_load_policy=0&cc_lang_pref=en&hl=en`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PLAY THE GAMES */}
      <section className="py-16" data-testid="games-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="flex items-end gap-4 mb-8 flex-wrap">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946]">// 01</div>
            <h2 className="font-display text-4xl sm:text-6xl leading-none flex items-center gap-3">
              <Gamepad2 className="w-9 h-9 text-[#FCA311]" /> PLAY THE GAMES
            </h2>
          </div>

          {!loading && games.length === 0 && (
            <div className="font-mono text-sm text-white/40 py-8" data-testid="games-empty">
              No games available yet.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="games-grid">
            {games.map((g) => {
              const meta = GAME_META[g.slug] || { Icon: Gamepad2, accent: "#FCA311", tagline: "" };
              const { Icon, accent, tagline } = meta;
              const playUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/games/${g.slug}/play`;
              return (
                <a
                  key={g.id}
                  href={playUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block rounded-3xl glass-card-strong ai-border float-card p-8 sm:p-10 min-h-[280px] overflow-hidden"
                  style={{ "--accent": accent }}
                  data-testid={`game-button-${g.slug}`}
                >
                  {/* Ambient accent orb */}
                  <div
                    className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-30 blur-3xl transition-all duration-500 group-hover:opacity-60 group-hover:scale-125"
                    style={{ backgroundColor: accent }}
                  />
                  {/* Grid dots background */}
                  <div
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                      backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
                      backgroundSize: "24px 24px",
                    }}
                  />
                  <div className="relative flex flex-col h-full">
                    <div className="flex items-center justify-between gap-4">
                      <div
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-2 flex items-center justify-center transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110"
                        style={{
                          borderColor: accent,
                          boxShadow: `0 0 28px ${accent}66, inset 0 0 16px ${accent}22`,
                        }}
                      >
                        <Icon className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: accent }} />
                      </div>
                      <div
                        className="font-mono text-[10px] tracking-[0.4em] px-3 py-1 rounded-full border"
                        style={{ borderColor: accent, color: accent }}
                      >
                        GAME 0{g.position || 1}
                      </div>
                    </div>

                    <h3 className="font-display text-4xl sm:text-5xl leading-none mt-6 group-hover:translate-x-1 transition-transform duration-300">
                      {g.title}
                    </h3>
                    {tagline && (
                      <p className="text-white/70 text-sm sm:text-base mt-3 max-w-md leading-relaxed">
                        {tagline}
                      </p>
                    )}

                    <div className="mt-auto pt-8 flex items-center gap-3 font-mono text-xs tracking-[0.3em]">
                      <span
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                        style={{
                          backgroundColor: accent,
                          boxShadow: `0 0 24px ${accent}`,
                        }}
                      >
                        <Play className="w-4 h-4 text-black fill-black" />
                      </span>
                      <span className="text-white/80 group-hover:text-white">
                        LAUNCH IN NEW WINDOW <ExternalLink className="inline-block w-3 h-3 ml-1 -mt-0.5" />
                      </span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* PAST STREAMS (Twitch VODs) */}
      {vods.length > 0 && (
        <section className="py-16" data-testid="vods-section">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
            <div className="flex items-end gap-4 mb-10 flex-wrap">
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#9146FF]">// 02</div>
              <h2 className="font-display text-4xl sm:text-6xl leading-none flex items-center gap-3">
                <Twitch className="w-9 h-9 text-[#9146FF]" /> PAST STREAMS
              </h2>
              <div className="font-mono text-xs text-white/40 ml-auto">{vods.length} VODs</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vods.map((v) => (
                <div key={v.id} className="glass-card-strong rounded-2xl overflow-hidden float-card" data-testid={`vod-${v.id}`}>
                  <div className="aspect-video bg-black">
                    <iframe
                      title={v.title || `Twitch VOD ${v.video_id}`}
                      src={`https://player.twitch.tv/?video=v${v.video_id}&parent=${parentHost}&autoplay=false`}
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  {v.title && (
                    <div className="px-4 py-3 font-mono text-xs text-white/70 truncate">{v.title}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CLIPS REEL */}
      <section className="py-16" data-testid="clips-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="flex items-end gap-4 mb-10 flex-wrap">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311]">// 03</div>
            <h2 className="font-display text-4xl sm:text-6xl leading-none flex items-center gap-3">
              <Youtube className="w-9 h-9 text-[#FF0033]" /> THE CLIP REEL
            </h2>
          </div>

          {Object.keys(clipsByGame).length === 0 && !loading && (
            <div className="font-mono text-sm text-white/40">No clips dropped yet.</div>
          )}

          <div className="space-y-12">
            {Object.entries(clipsByGame).map(([game, list]) => (
              <div key={game} data-testid={`clip-group-${game}`}>
                <div className="font-display text-2xl sm:text-3xl mb-4 border-b border-white/10 pb-2 flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-[#FCA311]" />
                  <span>{game}</span>
                  <span className="font-mono text-xs text-white/40 ml-auto">{list.length} CLIPS</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((c) => (
                    <div key={c.id} className="glass-card-strong rounded-2xl overflow-hidden float-card" data-testid={`clip-${c.id}`}>
                      <div className="aspect-video bg-black">
                        <iframe
                          title={c.title}
                          src={`https://www.youtube.com/embed/${c.youtube_id}?rel=0&modestbranding=1&playsinline=1&cc_load_policy=0&cc_lang_pref=en&hl=en`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      </div>
                      {c.title && (
                        <div className="px-4 py-3 font-mono text-xs text-white/70 truncate">{c.title}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SQUAD */}
      <section className="py-16" data-testid="squad-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="flex items-end gap-4 mb-10 flex-wrap">
            <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946]">// 04</div>
            <h2 className="font-display text-4xl sm:text-6xl leading-none flex items-center gap-3">
              <Users className="w-9 h-9 text-[#FCA311]" /> THE SQUAD
            </h2>
          </div>

          {squad.length === 0 && !loading && (
            <div className="font-mono text-sm text-white/40">No squad members posted yet.</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {squad.map((m) => {
              const pm = PLATFORM_META[m.platform] || PLATFORM_META.pc;
              return (
                <div key={m.id} className="glass-card glass-hover rounded-2xl p-5 float-card" data-testid={`squad-${m.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-black border border-white/20 flex-shrink-0 overflow-hidden">
                      {m.avatar_path ? (
                        <img src={imgUrl(m.avatar_path)} alt={m.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-display text-2xl text-white/40">
                          {m.name.slice(0, 1).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-2xl truncate">{m.name}</div>
                      {m.gamertag && (
                        <div className="font-mono text-xs text-white/50 truncate">@{m.gamertag}</div>
                      )}
                      <div className="mt-1 font-mono text-[10px] tracking-widest" style={{ color: pm.color }}>
                        {pm.label}
                      </div>
                    </div>
                  </div>
                  {m.favorite_games?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1">
                      {m.favorite_games.map((g) => (
                        <span key={g} className="font-mono text-[10px] tracking-wider border border-white/15 px-2 py-0.5 text-white/70">
                          {g}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex gap-3 text-white/60">
                    {m.twitch_handle && (
                      <a href={`https://twitch.tv/${m.twitch_handle}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#9146FF]" aria-label="Twitch">
                        <Twitch className="w-4 h-4" />
                      </a>
                    )}
                    {m.youtube_handle && (
                      <a href={`https://youtube.com/@${m.youtube_handle}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF0033]" aria-label="YouTube">
                        <Youtube className="w-4 h-4" />
                      </a>
                    )}
                    {m.discord_handle && (
                      <span className="font-mono text-[10px] text-white/40 inline-flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" /> {m.discord_handle}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DISCORD CTA */}
      <section className="py-16" data-testid="discord-cta">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
          <div className="relative overflow-hidden rounded-3xl glass-card-strong ai-border float-card p-8 sm:p-12 flex flex-col sm:flex-row items-center justify-between gap-8">
            <div className="orb pointer-events-none" style={{ width: 380, height: 380, top: -100, right: -60, background: "radial-gradient(circle, rgba(88,101,242,0.9), transparent 70%)", opacity: 0.45 }} />
            <div className="relative">
              <div className="font-mono text-[10px] tracking-[0.4em] text-[#5865F2] mb-2">// JOIN THE LOBBY</div>
              <h3 className="font-display text-4xl sm:text-6xl leading-none">DISCORD.</h3>
              <p className="text-white/70 mt-3 max-w-md">Squad up live. Drop in, mic up, talk shit.</p>
            </div>
            {config.discord_invite_url ? (
              <a
                href={config.discord_invite_url}
                target="_blank"
                rel="noopener noreferrer"
                className="relative font-display text-xl px-8 py-5 rounded-2xl bg-[#5865F2] text-white hover:bg-[#4752C4] inline-flex items-center gap-3 transition-colors shadow-[0_0_32px_rgba(88,101,242,0.55)]"
                data-testid="discord-join-btn"
              >
                <MessageCircle className="w-5 h-5" /> JOIN THE DISCORD
              </a>
            ) : (
              <div className="relative font-mono text-sm text-white/50">Discord invite coming soon.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
